import React, { useState, useEffect, useMemo, useId } from 'react';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { Vehicle } from '../types/vehicle';
import CustomDropdown from './CustomDropdown';
import VehicleFieldCombobox from './VehicleFieldCombobox';
import ExteriorFinishFields from './vehicle/ExteriorFinishFields';
import AdminEditingBanner from './ui/AdminEditingBanner';
import { isAdminEditingOthersVehicle } from '../utils/permissions';
import { exteriorFinishToPayload, normalizeExteriorFinishType, validateExteriorFinishInput } from '../utils/exteriorFinish';
import EditorChrome, { type EditorChromeVariant } from './ui/EditorChrome';
import Button from './ui/Button';
import { useToast } from '../contexts/ToastContext';
import {
  sanitizePublicSlugInput,
  sanitizePublicSlugTyping,
  validatePublicSlug,
} from '../utils/vehicleUrls';

interface EditVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle;
  onVehicleUpdated: (updatedVehicle: Vehicle) => void;
  /** `panel` embeds the form inside Manage build without a nested dialog. */
  variant?: EditorChromeVariant;
}

const DRIVETRAIN_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'FWD', label: 'FWD' },
  { value: 'RWD', label: 'RWD' },
  { value: 'AWD', label: 'AWD' },
  { value: '4WD', label: '4WD' },
];

const BUILD_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
];

/** Prefer top-level specs, then nested performance block from public vehicle payloads. */
function resolvePerfNumber(
  vehicle: Vehicle,
  key: 'horsepower' | 'torque' | 'redline'
): number | '' {
  const top = vehicle[key];
  if (typeof top === 'number' && !Number.isNaN(top)) return top;
  const nested = vehicle.performance?.[key];
  if (typeof nested === 'number' && !Number.isNaN(nested)) return nested;
  return '';
}

function resolvePowerMeasurement(vehicle: Vehicle): '' | 'wheel' | 'crank' {
  const top = vehicle.horsepower_measurement;
  if (top === 'wheel' || top === 'crank') return top;
  const nested = vehicle.performance?.horsepower_measurement;
  if (nested === 'wheel' || nested === 'crank') return nested;
  return '';
}

function buildEditFormState(vehicle: Vehicle) {
  return {
    year: vehicle.year,
    make: vehicle.make,
    model: vehicle.model,
    trim: vehicle.trim,
    chassis: vehicle.chassis || '',
    engine: vehicle.engine || '',
    transmission: vehicle.transmission || '',
    drivetrain: vehicle.drivetrain || '',
    build_status: vehicle.build_status || 'in_progress',
    color: vehicle.color || '',
    exterior_finish_type: normalizeExteriorFinishType(vehicle.exterior_finish_type),
    exterior_finish_color: vehicle.exterior_finish_color || '',
    exterior_finish_brand: vehicle.exterior_finish_brand || '',
    exterior_finish_style: vehicle.exterior_finish_style || '',
    nickname: vehicle.nickname || '',
    description: vehicle.description || '',
    public_slug: sanitizePublicSlugInput(
      vehicle.public_slug || vehicle.slug || ''
    ),
    is_primary: Boolean(vehicle.is_primary),
    is_public: vehicle.is_public !== false,
    horsepower: resolvePerfNumber(vehicle, 'horsepower'),
    horsepower_measurement: resolvePowerMeasurement(vehicle),
    torque: resolvePerfNumber(vehicle, 'torque'),
    redline: resolvePerfNumber(vehicle, 'redline'),
    show_pricing: vehicle.settings?.show_pricing ?? true,
    show_performance: vehicle.settings?.show_performance ?? true,
  };
}

const EditVehicleModal: React.FC<EditVehicleModalProps> = ({
  isOpen,
  onClose,
  vehicle,
  onVehicleUpdated,
  variant = 'modal',
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const fieldId = useId();
  const formDomId = `${fieldId}-edit-vehicle-form`;
  const isPanel = variant === 'panel';
  const ownerUsername = vehicle.owner_username || user?.username || 'you';
  const [formData, setFormData] = useState(() => buildEditFormState(vehicle));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustomMake, setIsCustomMake] = useState(false);
  const [customMake, setCustomMake] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Seed form only when the modal opens — not on every parent re-render of `vehicle`. */
  const [formSeeded, setFormSeeded] = useState(false);
  
  // Track original values for rarity-affecting fields
  const [originalRarityFields, setOriginalRarityFields] = useState({
    year: vehicle.year,
    make: vehicle.make,
    model: vehicle.model,
    trim: vehicle.trim,
    color: vehicle.color || ''
  });

  // Popular makes for suggestions
  const popularMakes = useMemo(() => [
    'Acura', 'Audi', 'BMW', 'Chevrolet', 'Dodge', 'Ford', 'Honda', 'Hyundai',
    'Infiniti', 'Lexus', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'Nissan',
    'Porsche', 'Subaru', 'Toyota', 'Volkswagen', 'Volvo'
  ], []);

  // Check if rarity-affecting fields have changed
  const rarityFieldsChanged = useMemo(() => {
    return (
      formData.year !== originalRarityFields.year ||
      formData.make !== originalRarityFields.make ||
      formData.model !== originalRarityFields.model ||
      formData.trim !== originalRarityFields.trim ||
      formData.color !== originalRarityFields.color
    );
  }, [formData, originalRarityFields]);

  const identityFieldDiffs = useMemo(() => {
    const fields: { key: keyof typeof originalRarityFields; label: string }[] = [
      { key: 'year', label: 'Year' },
      { key: 'make', label: 'Make' },
      { key: 'model', label: 'Model' },
      { key: 'trim', label: 'Trim' },
      { key: 'color', label: 'Color' },
    ];
    return fields
      .filter(({ key }) => formData[key] !== originalRarityFields[key])
      .map(({ key, label }) => ({
        label,
        from: String(originalRarityFields[key] || '-'),
        to: String(formData[key] || '-'),
      }));
  }, [formData, originalRarityFields]);

  useEffect(() => {
    if (!isOpen) {
      setFormSeeded(false);
      return;
    }
    // Parent often passes a fresh `vehicle` object literal each render. Re-seeding
    // on that identity change wipes in-progress edits (fields "snap back").
    if (formSeeded) return;

    const next = buildEditFormState(vehicle);
    setFormData(next);
    setOriginalRarityFields({
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      color: vehicle.color || '',
    });
    const makeInList = popularMakes.includes(vehicle.make);
    setIsCustomMake(!makeInList);
    setCustomMake(makeInList ? '' : vehicle.make);
    setFieldErrors({});
    setError(null);
    setFormSeeded(true);
  }, [isOpen, formSeeded, vehicle, popularMakes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.nickname.trim()) {
      setError('Nickname is required');
      return;
    }

    const finishType = normalizeExteriorFinishType(formData.exterior_finish_type);
    const finishError = validateExteriorFinishInput(finishType, formData.exterior_finish_color);
    if (finishError) {
      setFieldErrors({ exterior_finish_color: finishError });
      setError(finishError);
      return;
    }

    const nextSlug = sanitizePublicSlugInput(formData.public_slug);
    const hadSlug = Boolean(
      sanitizePublicSlugInput(vehicle.public_slug || vehicle.slug || '')
    );
    // Public builds (or any build that already has a URL) must keep a valid slug.
    const isPublic = isPanel ? vehicle.is_public !== false : formData.is_public;
    const slugRequired = isPublic || hadSlug;
    if (slugRequired || nextSlug) {
      const slugError = validatePublicSlug(nextSlug);
      if (slugError) {
        setFieldErrors({ public_slug: slugError });
        setError(slugError);
        return;
      }
    }
    
    if (formData.horsepower !== '' && formData.horsepower != null) {
      if (formData.horsepower_measurement !== 'wheel' && formData.horsepower_measurement !== 'crank') {
        setFieldErrors({ horsepower_measurement: 'Choose wheel (whp) or crank (hp).' });
        setError('Choose wheel (whp) or crank (hp) for peak power.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setFieldErrors({});

    try {
      const parsePerf = (value: number | '') =>
        value === '' || value === null || value === undefined ? '' : Number(value);
      const parsedHp = parsePerf(formData.horsepower);

      const updatedVehicle = await apiService.updateVehicle(vehicle.id, {
        year: formData.year,
        make: formData.make.trim(),
        model: formData.model.trim(),
        trim: formData.trim.trim(),
        color: formData.color.trim() || undefined,
        chassis: formData.chassis.trim() || undefined,
        engine: formData.engine.trim() || undefined,
        transmission: formData.transmission.trim() || undefined,
        drivetrain: formData.drivetrain.trim() || undefined,
        build_status: formData.build_status as 'planned' | 'in_progress' | 'complete',
        ...exteriorFinishToPayload({
          exterior_finish_type: finishType,
          exterior_finish_color: formData.exterior_finish_color,
          exterior_finish_brand: formData.exterior_finish_brand,
          exterior_finish_style: formData.exterior_finish_style,
        }),
        nickname: formData.nickname.trim() || undefined,
        description: formData.description.trim(),
        ...(nextSlug ? { public_slug: nextSlug } : {}),
        is_primary: formData.is_primary,
        // In Manage build, Sharing owns public visibility — omit so a stale
        // checkbox cannot overwrite an autosaved Sharing toggle.
        ...(isPanel ? {} : { is_public: formData.is_public }),
        // Empty string clears peak figures server-side (isset + non-numeric → null).
        horsepower: parsedHp,
        horsepower_measurement:
          parsedHp === ''
            ? null
            : (formData.horsepower_measurement as 'wheel' | 'crank'),
        torque: parsePerf(formData.torque),
        redline: parsePerf(formData.redline),
      });

      // Display toggles live in Advanced inside Manage build; keep them here for standalone modal.
      if (!isPanel) {
        try {
          await apiService.updateVehicleSettings(vehicle.id, {
            show_pricing: formData.show_pricing,
            show_performance: formData.show_performance,
          });
        } catch (settingsError) {
          console.error('Failed to update vehicle settings:', settingsError);
        }
      }

      updatedVehicle.settings = {
        show_pricing: isPanel
          ? (vehicle.settings?.show_pricing ?? formData.show_pricing)
          : formData.show_pricing,
        show_performance: isPanel
          ? (vehicle.settings?.show_performance ?? formData.show_performance)
          : formData.show_performance,
      };
      if (typeof updatedVehicle.is_public !== 'boolean') {
        updatedVehicle.is_public = isPanel ? vehicle.is_public !== false : formData.is_public;
      }
      if (typeof updatedVehicle.is_primary !== 'boolean') {
        updatedVehicle.is_primary = formData.is_primary;
      }

      // Ensure assessment_status is set to 'pending' if missing (for async AI assessment)
      if (!updatedVehicle.assessment_status) {
        updatedVehicle.assessment_status = 'pending';
      }
      showToast('Changes saved.', 'success');
      onVehicleUpdated(updatedVehicle);
      if (variant === 'modal') onClose();
      return;
    } catch (error: any) {
      console.error('Failed to update vehicle:', error);
      setError(error.message || 'Failed to update vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Handle make selection
    if (field === 'make') {
      if (value === 'custom') {
        setIsCustomMake(true);
        setFormData(prev => ({ ...prev, make: customMake }));
      } else {
        setIsCustomMake(false);
        setCustomMake('');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <EditorChrome
      variant={variant}
      isOpen={isOpen}
      onClose={() => { if (!loading) onClose(); }}
      title="Edit vehicle"
      description="Update identity and build details. Saving identity changes may re-queue Collectibility assessment."
      size="xl"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      showClose={!loading}
      footer={
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          {variant === 'modal' ? (
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading} className="flex-1 min-h-[44px]">
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            form={formDomId}
            variant="primary"
            disabled={loading}
            loading={loading}
            loadingLabel="Saving…"
            className="flex-1 min-h-[44px]"
          >
            Save vehicle
          </Button>
        </div>
      }
    >
{isAdminEditingOthersVehicle(user, vehicle) && (
            <AdminEditingBanner
              contentLabel="vehicle"
              ownerLabel={
                vehicle.owner_username
                  ? `@${vehicle.owner_username}`
                  : 'another user'
              }
              className="mb-4"
            />
          )}

          {/* Rarity Re-assessment Warning */}
          {rarityFieldsChanged && (
            <div className="mb-6 p-4 bg-amber-900/50 border border-amber-700 rounded-lg">
              <div className="flex items-start space-x-3">
                <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="flex-1">
                  <p className="text-amber-200 font-medium">Collectibility re-assessment</p>
                  <p className="text-amber-300 text-sm mb-3">
                    These identity fields changed. Saving will queue a new Collectibility Index run (usually completes in the background).
                  </p>
                  <ul className="text-xs space-y-1">
                    {identityFieldDiffs.map((diff) => (
                      <li key={diff.label} className="text-amber-100/90 font-mono">
                        <span className="text-amber-400">{diff.label}:</span>{' '}
                        <span className="line-through opacity-70">{diff.from}</span>
                        {' → '}
                        <span className="text-white">{diff.to}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <form id={formDomId} onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Year Field */}
              <div>
                <label htmlFor={`${fieldId}-year`} className="input-label">
                  Year *
                </label>
                <input
                  type="number"
                  id={`${fieldId}-year`}
                  name="year"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', parseInt(e.target.value) || '')}
                  className="input"
                  min="1980"
                  max={new Date().getFullYear() + 2}
                  required
                  disabled={loading}
                />
              </div>

              {/* Make Field */}
              <div>
                <label htmlFor="make" className="input-label">
                  Make *
                </label>
                {isCustomMake ? (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={formData.make}
                      onChange={(e) => handleInputChange('make', e.target.value)}
                      className="input flex-1"
                      placeholder="Enter make"
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setIsCustomMake(false)}
                      className="px-3 py-2 bg-[#1a1a1a] hover:bg-white/[0.06] text-white rounded-lg transition-colors"
                      disabled={loading}
                    >
                      Select
                    </button>
                  </div>
                ) : (
                  <CustomDropdown
                    value={formData.make}
                    onChange={(value) => handleInputChange('make', value)}
                    options={[
                      ...popularMakes.map(make => ({ value: make, label: make })),
                      { value: 'custom', label: 'Other (Custom)' }
                    ]}
                    placeholder="Select Make"
                    disabled={loading}
                  />
                )}
              </div>

              {/* Model Field */}
              <div>
                <label htmlFor="model" className="input-label">
                  Model *
                </label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  className="input"
                  placeholder="e.g., Lancer Evolution"
                  required
                  disabled={loading}
                />
              </div>

              {/* Trim Field */}
              <div>
                <label htmlFor="trim" className="input-label">
                  Trim *
                </label>
                <input
                  type="text"
                  id="trim"
                  name="trim"
                  value={formData.trim}
                  onChange={(e) => handleInputChange('trim', e.target.value)}
                  className="input"
                  placeholder="e.g., 9 GT Wagon"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <ExteriorFinishFields
              values={formData}
              onChange={(field, value) => handleInputChange(field, value)}
              disabled={loading}
              errors={fieldErrors}
            />

            {/* Build specs */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Build specs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="chassis" className="input-label">
                    Chassis
                  </label>
                  <VehicleFieldCombobox
                    field="chassis"
                    id="chassis"
                    value={formData.chassis}
                    onChange={(v) => handleInputChange('chassis', v)}
                    placeholder="e.g., WC34"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="engine" className="input-label">
                    Engine
                  </label>
                  <VehicleFieldCombobox
                    field="engine"
                    id="engine"
                    value={formData.engine}
                    onChange={(v) => handleInputChange('engine', v)}
                    placeholder="e.g., RB26DETT"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="transmission" className="input-label">
                    Transmission
                  </label>
                  <VehicleFieldCombobox
                    field="transmission"
                    id="transmission"
                    value={formData.transmission}
                    onChange={(v) => handleInputChange('transmission', v)}
                    placeholder="e.g., 5-speed manual"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="drivetrain" className="input-label">
                    Drivetrain
                  </label>
                  <CustomDropdown
                    value={formData.drivetrain}
                    onChange={(value) => handleInputChange('drivetrain', value)}
                    options={DRIVETRAIN_OPTIONS}
                    placeholder="Select Drivetrain"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="build_status" className="input-label">
                    Build status
                  </label>
                  <CustomDropdown
                    value={formData.build_status}
                    onChange={(value) => handleInputChange('build_status', value)}
                    options={BUILD_STATUS_OPTIONS}
                    placeholder="Select Build Status"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor={`${fieldId}-nickname`} className="input-label">
                Nickname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id={`${fieldId}-nickname`}
                name="nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                className="input"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor={`${fieldId}-public_slug`} className="input-label">
                Build URL
              </label>
              <div className="flex items-stretch rounded-lg border border-gray-600 bg-gray-900 overflow-hidden focus-within:ring-2 focus-within:ring-orange-500/50">
                <span className="hidden sm:inline-flex items-center px-3 text-xs text-gray-400 font-mono whitespace-nowrap border-r border-gray-600">
                  /{ownerUsername}/
                </span>
                <input
                  type="text"
                  id={`${fieldId}-public_slug`}
                  name="public_slug"
                  value={formData.public_slug}
                  onChange={(e) => {
                    const next = sanitizePublicSlugTyping(e.target.value);
                    handleInputChange('public_slug', next);
                    if (fieldErrors.public_slug) {
                      setFieldErrors((prev) => {
                        const { public_slug: _removed, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== ' ' && e.code !== 'Space') return;
                    e.preventDefault();
                    const input = e.currentTarget;
                    const start = input.selectionStart ?? formData.public_slug.length;
                    const end = input.selectionEnd ?? start;
                    const next = sanitizePublicSlugTyping(
                      `${formData.public_slug.slice(0, start)}-${formData.public_slug.slice(end)}`
                    );
                    handleInputChange('public_slug', next);
                    // Restore caret after the inserted hyphen on the next paint.
                    requestAnimationFrame(() => {
                      const pos = Math.min(start + 1, next.length);
                      input.setSelectionRange(pos, pos);
                    });
                  }}
                  onBlur={() => {
                    const finalized = sanitizePublicSlugInput(formData.public_slug);
                    if (finalized !== formData.public_slug) {
                      handleInputChange('public_slug', finalized);
                    }
                    const slugRequired =
                      (isPanel ? vehicle.is_public !== false : formData.is_public) ||
                      Boolean(sanitizePublicSlugInput(vehicle.public_slug || vehicle.slug || ''));
                    if (!slugRequired && !finalized) {
                      setFieldErrors((prev) => {
                        const { public_slug: _removed, ...rest } = prev;
                        return rest;
                      });
                      return;
                    }
                    const slugError = validatePublicSlug(finalized);
                    setFieldErrors((prev) => {
                      if (slugError) return { ...prev, public_slug: slugError };
                      const { public_slug: _removed, ...rest } = prev;
                      return rest;
                    });
                  }}
                  className="input border-0 rounded-none flex-1 font-mono focus:ring-0"
                  placeholder="my-build"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={Boolean(fieldErrors.public_slug)}
                  aria-describedby={`${fieldId}-public_slug_help`}
                  disabled={loading}
                />
              </div>
              <p id={`${fieldId}-public_slug_help`} className="mt-2 text-xs text-gray-400">
                Preview:{' '}
                <span className="font-mono text-gray-300">
                  /{ownerUsername}/{sanitizePublicSlugInput(formData.public_slug) || '…'}
                </span>
                . Use letters, numbers, and hyphens (space inserts a hyphen). Changing this updates your
                public link; previous URLs keep working for QR codes and bookmarks.
              </p>
              {fieldErrors.public_slug && (
                <p className="mt-1 text-sm text-red-400" role="alert">
                  {fieldErrors.public_slug}
                </p>
              )}
            </div>

            <div>
              <label htmlFor={`${fieldId}-description`} className="input-label">
                Description
              </label>
              <textarea
                id={`${fieldId}-description`}
                name="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="input"
                disabled={loading}
              />
            </div>

            {/* Performance Section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Peak figures</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor={`${fieldId}-horsepower`} className="input-label">
                    Peak Power (
                    {formData.horsepower_measurement === 'wheel' ? 'whp' : 'HP'})
                  </label>
                  <input
                    type="number"
                    id={`${fieldId}-horsepower`}
                    name="horsepower"
                    value={formData.horsepower}
                    onChange={(e) =>
                      handleInputChange(
                        'horsepower',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    min="0"
                    max="9999"
                    placeholder="e.g., 400"
                    className="input"
                    disabled={loading}
                    aria-describedby={`${fieldId}-horsepower-measurement-help`}
                  />
                  <fieldset className="mt-3">
                    <legend className="input-label mb-1">Power measurement</legend>
                    <div
                      className="flex flex-wrap gap-4"
                      role="radiogroup"
                      aria-label="Peak power measurement"
                    >
                      {(['wheel', 'crank'] as const).map((loc) => (
                        <label
                          key={loc}
                          className="inline-flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-gray-200"
                        >
                          <input
                            type="radio"
                            name={`${fieldId}-horsepower_measurement`}
                            value={loc}
                            checked={formData.horsepower_measurement === loc}
                            onChange={() =>
                              handleInputChange('horsepower_measurement', loc)
                            }
                            className="checkbox-control"
                            disabled={loading}
                          />
                          {loc === 'wheel' ? 'Wheel (whp)' : 'Crank (hp)'}
                        </label>
                      ))}
                    </div>
                    {fieldErrors.horsepower_measurement ? (
                      <p className="mt-1 text-sm text-red-400" role="alert">
                        {fieldErrors.horsepower_measurement}
                      </p>
                    ) : (
                      <p
                        id={`${fieldId}-horsepower-measurement-help`}
                        className="mt-1 text-xs text-gray-500"
                      >
                        Required when peak power is set. Shown as whp or HP on the build.
                      </p>
                    )}
                  </fieldset>
                </div>

                <div>
                  <label htmlFor={`${fieldId}-torque`} className="input-label">
                    Peak Torque (lb-ft)
                  </label>
                  <input
                    type="number"
                    id={`${fieldId}-torque`}
                    name="torque"
                    value={formData.torque}
                    onChange={(e) =>
                      handleInputChange(
                        'torque',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    min="0"
                    max="9999"
                    placeholder="e.g., 350"
                    className="input"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor={`${fieldId}-redline`} className="input-label">
                    RPM Redline
                  </label>
                  <input
                    type="number"
                    id={`${fieldId}-redline`}
                    name="redline"
                    value={formData.redline}
                    onChange={(e) =>
                      handleInputChange(
                        'redline',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    min="0"
                    max="15000"
                    placeholder="e.g., 7000"
                    className="input"
                    disabled={loading}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Shown on the build page when performance stats are enabled. Clear a field and save to remove it.
              </p>
            </div>

            {!isPanel ? (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Display Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <label htmlFor={`${fieldId}-show_pricing`} className="input-label mb-0">
                        Show Pricing Information
                      </label>
                      <p className="input-helper mt-0">
                        Display total investment and individual modification costs to visitors
                      </p>
                    </div>
                    <button
                      type="button"
                      id={`${fieldId}-show_pricing`}
                      role="switch"
                      aria-checked={formData.show_pricing}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, show_pricing: !prev.show_pricing }))
                      }
                      className={`toggle-track ${formData.show_pricing ? 'toggle-track-on' : ''}`}
                      disabled={loading}
                    >
                      <span
                        className={`toggle-thumb ${formData.show_pricing ? 'toggle-thumb-on' : ''}`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <label htmlFor={`${fieldId}-show_performance`} className="input-label mb-0">
                        Show Performance Stats
                      </label>
                      <p className="input-helper mt-0">
                        Display horsepower, torque, and redline information to visitors
                      </p>
                    </div>
                    <button
                      type="button"
                      id={`${fieldId}-show_performance`}
                      role="switch"
                      aria-checked={formData.show_performance}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          show_performance: !prev.show_performance,
                        }))
                      }
                      className={`toggle-track ${formData.show_performance ? 'toggle-track-on' : ''}`}
                      disabled={loading}
                    >
                      <span
                        className={`toggle-thumb ${
                          formData.show_performance ? 'toggle-thumb-on' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Pricing and performance visibility are in Advanced. Public link and visitor toggles are in
                Sharing.
              </p>
            )}

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`${fieldId}-is_primary`}
                  name="is_primary"
                  checked={formData.is_primary}
                  onChange={(e) => handleInputChange('is_primary', e.target.checked)}
                  className="checkbox-control"
                  disabled={loading}
                />
                <label htmlFor={`${fieldId}-is_primary`} className="ml-2 text-sm text-gray-300">
                  Set as primary vehicle in your garage
                </label>
              </div>

              {!isPanel ? (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${fieldId}-is_public`}
                    name="is_public"
                    checked={formData.is_public}
                    onChange={(e) => handleInputChange('is_public', e.target.checked)}
                    className="checkbox-control"
                    disabled={loading}
                  />
                  <label htmlFor={`${fieldId}-is_public`} className="ml-2 text-sm text-gray-300">
                    Make vehicle public (visible to others)
                  </label>
                </div>
              ) : null}
            </div>

          </form>
    </EditorChrome>
  );
};

export default EditVehicleModal; 